/**
 * Typed client for /api/chat — the Ask ABHI assistant.
 *
 * Mirrors hy_sales/schemas/chat.py — keep the types in lockstep when the
 * backend changes. Citations are produced by
 * hy_sales/services/chat/sources.py (Source.to_json).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiPost, apiPostStream } from './client';

export type ChatStatusValue = 'answered' | 'refused' | 'clarify' | 'unverified' | 'error';

export interface ChatStatus {
  enabled: boolean;
  can_depletions: boolean;
  can_notes: boolean;
  daily_limit: number;
  used_today: number;
  suggestions: string[];
  /** ISO date the depletions data covers through. */
  data_through: string | null;
  /** Territory state codes; empty = all states. */
  territories: string[];
}

export type ChatSuggestionKind =
  | 'growth'
  | 'decline'
  | 'risk'
  | 'new'
  | 'product'
  | 'channel'
  | 'notes';

/** A data-driven starter prompt. Mirrors schemas/chat.py ChatSuggestion. */
export interface ChatSuggestion {
  kind: ChatSuggestionKind;
  /** Full sentence behind the prompt — shown on hover. */
  fact: string;
  /** Sent to the assistant when picked. */
  question: string;
  /** Short one-line version for the list row. */
  label: string;
  /** Compact tag: "+75%", "−86%", "since Mar", "115 new". */
  metric: string;
  tone: 'up' | 'down' | 'warn' | 'info';
}

export interface ChatNoteDetail {
  note_id: number;
  visit_date: string;
  rep: string;
  account_id: number;
  account: string;
  city: string | null;
  state: string | null;
  channel: string;
  outcome: string;
  products_discussed: string[];
  text: string;
}

export interface ChatCitation {
  id: string;
  kind: 'data' | 'note' | 'lookup';
  tool: string;
  title: string;
  detail: string;
  /** Few-word name for the inline citation chip. */
  short?: string;
  params: Record<string, unknown>;
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  data_through: string | null;
  files: { filename: string; loaded_at: string | null; data_through: string | null }[];
  link: string | null;
  note: ChatNoteDetail | null;
}

/** One line of the "how I answered" list. Mirrors schemas/chat.py ChatStep. */
export interface ChatStep {
  label: string;
  detail: string | null;
  ok: boolean;
}

/** A live progress event from POST /api/chat/ask/stream. */
export interface ChatLiveStep {
  key: string;
  label: string;
  state: 'active' | 'done' | 'error';
  detail: string | null;
}

type StreamEvent =
  | ({ type: 'step' } & ChatLiveStep)
  | { type: 'done'; response: ChatAskResponse }
  | { type: 'error'; message: string; conversation_id?: number | null };

/** A failed answer. The question may already be saved in `conversationId`. */
export class ChatStreamError extends Error {
  constructor(
    message: string,
    readonly conversationId: number | null,
  ) {
    super(message);
  }
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  status: ChatStatusValue | null;
  citations: ChatCitation[];
  steps: ChatStep[];
  latency_ms: number | null;
  /** Suggested next questions — only on a freshly returned answer. */
  follow_ups?: string[];
  feedback_rating: number | null;
  feedback_reasons?: string[];
  feedback_comment?: string | null;
  created_at: string;
}

export interface ChatConversationSummary {
  id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  /** Questions in the thread (history list only). */
  question_count: number;
  /** First line of the latest answer, plain text (history list only). */
  preview: string | null;
}

export interface ChatConversationDetail extends ChatConversationSummary {
  messages: ChatMessage[];
}

export interface ChatAskResponse {
  conversation: ChatConversationSummary;
  user_message: ChatMessage;
  assistant_message: ChatMessage;
}

const STATUS_KEY = ['chat', 'status'] as const;
const CONVERSATIONS_KEY = ['chat', 'conversations'] as const;
const conversationKey = (id: number) => ['chat', 'conversation', id] as const;

export function useChatStatus(enabled: boolean) {
  return useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => apiGet<ChatStatus>('sales', '/api/chat/status'),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** Starter prompts computed from current data; fetched when the panel opens. */
export function useChatSuggestions(enabled: boolean) {
  return useQuery({
    queryKey: ['chat', 'suggestions'],
    queryFn: () => apiGet<ChatSuggestion[]>('sales', '/api/chat/suggestions'),
    enabled,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useChatConversations(enabled: boolean) {
  return useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => apiGet<ChatConversationSummary[]>('sales', '/api/chat/conversations'),
    enabled,
  });
}

export function useChatConversation(id: number | null) {
  return useQuery({
    queryKey: conversationKey(id ?? 0),
    queryFn: () => apiGet<ChatConversationDetail>('sales', `/api/chat/conversations/${id}`),
    enabled: id !== null,
  });
}

/** Ask via the streaming endpoint; `onStep` fires as the server works. */
async function askStream(vars: {
  conversation_id: number | null;
  content: string;
  onStep: (step: ChatLiveStep) => void;
}): Promise<ChatAskResponse> {
  // Assigned inside the callback, so held on an object (TS can't track
  // closure writes to a local and would narrow it to null).
  const out: {
    result: ChatAskResponse | null;
    failure: string | null;
    conversationId: number | null;
  } = { result: null, failure: null, conversationId: null };
  await apiPostStream(
    'sales',
    '/api/chat/ask/stream',
    { conversation_id: vars.conversation_id, content: vars.content },
    (raw) => {
      const event = raw as StreamEvent;
      if (event.type === 'step') {
        vars.onStep({
          key: event.key,
          label: event.label,
          state: event.state,
          detail: event.detail,
        });
      } else if (event.type === 'done') out.result = event.response;
      else {
        out.failure = event.message;
        out.conversationId = event.conversation_id ?? null;
      }
    },
  );
  if (out.result) return out.result;
  throw new ChatStreamError(
    out.failure ?? 'The answer stream ended unexpectedly.',
    out.conversationId,
  );
}

export function useAskChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: askStream,
    onSuccess: (res) => {
      queryClient.setQueryData<ChatConversationDetail>(
        conversationKey(res.conversation.id),
        (prev) => ({
          ...res.conversation,
          messages: [...(prev?.messages ?? []), res.user_message, res.assistant_message],
        }),
      );
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    },
    onError: (e) => {
      // The question was saved (and counted) even though the answer failed.
      if (e instanceof ChatStreamError && e.conversationId !== null) {
        void queryClient.invalidateQueries({ queryKey: conversationKey(e.conversationId) });
      }
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });
}

export function useArchiveConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete<void>('sales', `/api/chat/conversations/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
}

/** Thumbs-down reasons; must match ChatFeedbackReason in the sales API. */
export type ChatFeedbackReason =
  | 'wrong_numbers'
  | 'not_what_i_asked'
  | 'missing_data'
  | 'hard_to_read'
  | 'too_slow'
  | 'other';

/** Set, change or clear (`rating: null`) the rating on one answer. */
export function sendChatFeedback(
  messageId: number,
  feedback: { rating: 1 | -1 | null; reasons?: ChatFeedbackReason[]; comment?: string },
): Promise<void> {
  return apiPost<void>('sales', `/api/chat/messages/${messageId}/feedback`, feedback);
}
